import { Routes } from '@angular/router';
import { permissionGuard } from '@/app/core/guards/permission.guard';
import { AuthLayoutComponent } from '@/app/layouts/auth-layout/auth-layout.component';
import { MainLayoutComponent } from '@/app/layouts/main-layout/main-layout.component';
import { PERMISSION_LIST } from '@/core/constants/constants';
import { nonAuthGuard } from '@/core/guards/non-auth.guard';
import { authGuard } from '@/core/guards/auth-guard';

export const routes: Routes = [
  // 🔐 AUTH
  {
    path: '',
    component: AuthLayoutComponent,
    canActivateChild: [nonAuthGuard],
    children: [
      {
        path: '',
        loadChildren: () => import('@/app/core/auth/auth.routes').then((m) => m.AuthRoutes),
      },
    ],
  },

  // 🏠 MAIN APP
  {
    path: '',
    component: MainLayoutComponent,
    canActivateChild: [authGuard, permissionGuard],
    children: [
      // ✅ DEFAULT ROUTE
      {
        path: '',
        redirectTo: 'countries',
        pathMatch: 'full',
      },
      {
        path: 'access-denied',
        loadComponent: () =>
          import('@/app/shared/access-denied/access-denied.component').then(
            (m) => m.AccessDeniedComponent,
          ),
      },
      // 🌍 Location

      {
        path: 'countries',
        loadChildren: () =>
          import('@/app/modules/countries/countries.routes').then((m) => m.countryRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.LOCATION.COUNTRY_VIEW] },
      },
      {
        path: 'states',
        loadChildren: () => import('@/app/modules/states/states.routes').then((m) => m.stateRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.LOCATION.STATE_VIEW] },
      },
      {
        path: 'cities',
        loadChildren: () => import('@/app/modules/cities/cities.routes').then((m) => m.cityRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.LOCATION.CITY_VIEW] },
      },

      // 🔐 Access Control
      {
        path: 'users',
        loadChildren: () => import('@/app/modules/users/users.routes').then((m) => m.userRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.USER.VIEW] },
      },
      {
        path: 'dashboard-component',
        loadChildren: () =>
          import('@/app/modules/dashboard-component/dashboard.routes').then(
            (m) => m.dashboardComponentRoutes,
          ),
        data: { PERMISSION_LIST: [PERMISSION_LIST.DASHBOARD.VIEW] },
      },
      // {
      //   path: 'dashboard',
      //   loadChildren: () =>
      //     import('@/app/modules/dashboard/dashboard.routes').then((m) => m.dashboardRoutes),
      //   data: { PERMISSION_LIST: [PERMISSION_LIST.DASHBOARD.SUPER_ADMIN] },
      // },
      {
        path: 'lead',
        loadChildren: () => import('@/app/modules/lead/lead.routes').then((m) => m.leadRoutes),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.LEAD.VIEW],
        },
      },
      {
        path: 'duplicate-leads',
        loadChildren: () =>
          import('@/app/modules/duplicate-leads/duplicate-leads.routes').then(
            (m) => m.duplicateLeadsRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.LEAD.VIEW],
        },
      },
      {
        path: 'pending-lead-queue',
        loadChildren: () =>
          import('@/app/modules/pending-lead-queue/pending-lead-queue.routes').then(
            (m) => m.pendingLeadQueueRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.LEAD_ASSIGNMENT_MODULE.VIEW],
        },
      },
      {
        path: 'callback-queue',
        loadChildren: () =>
          import('@/app/modules/callback-queue/callback-queue.routes').then(
            (m) => m.callbackQueueRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.AGENT_LEAD.VIEW],
          AGENT_ONLY: true,
        },
      },
      // {
      //   path: 'lead-upload',
      //   loadChildren: () =>
      //     import('@/app/modules/lead-upload/lead-upload.routes').then((m) => m.leadUploadRoutes),
      //   data: {
      //     PERMISSION_LIST: [PERMISSION_LIST.ADMIN.ACCESS],
      //   },
      // },

      {
        path: 'my-leads',
        loadChildren: () =>
          import('@/app/modules/my-leads/my-leads.routes').then((m) => m.myLeadsRoutes),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.AGENT_LEAD.VIEW],
        },
      },

      {
        path: 'roles',
        loadChildren: () => import('@/app/modules/roles/roles.routes').then((m) => m.roleRoutes),
        data: { PERMISSION_LIST: [PERMISSION_LIST.ROLE.VIEW] },
      },

      {
        path: 'lead-sources',
        loadChildren: () =>
          import('@/app/modules/lead-source/lead-source.routes').then((m) => m.leadSourceRoutes),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.LEAD_SOURCE.VIEW],
        },
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('@/app/modules/settings/settings.routes').then((m) => m.settingsRoutes),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.DISTRIBUTION_SETTINGS.VIEW],
        },
      },
      {
        path: 'developer-settings',
        loadChildren: () =>
          import('@/app/modules/developer-settings/developer-settings.routes').then(
            (m) => m.developerSettingsRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.DEVELOPER_SETTINGS.MANAGE],
          STRICT_PERMISSION: true,
        },
      },
      {
        path: 'recording',
        loadChildren: () =>
          import('@/app/modules/recording/recording.routes').then((m) => m.recordingRoutes),
        data: {
          PERMISSION_LIST: [
            PERMISSION_LIST.RECORDING.PLAYBACK_ALL,
            PERMISSION_LIST.RECORDING.PLAYBACK_OWN,
          ],
        },
      },
      /* {
        path: 'customer-call-logs',
        loadChildren: () =>
          import('@/app/modules/customer-call-logs/customer-call-logs.routes').then(
            (m) => m.customerCallLogsRoutes,
          ),
        data: {
          PERMISSION_LIST: [],
        },
      }, */
      {
        path: 'agent-performance',
        loadChildren: () =>
          import('@/app/modules/agent-performance/agent-performance.routes').then(
            (m) => m.agentPerformanceRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.AGENT_PERFORMANCE_REPORT.VIEW],
        },
      },
      {
        path: 'call-disposition-report',
        loadChildren: () =>
          import('@/app/modules/call-disposition-report/call-disposition-report.routes').then(
            (m) => m.callDispositionReportRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.CALL_DISPOSITION_REPORT.VIEW],
        },
      },
      {
        path: 'conversion-report',
        loadChildren: () =>
          import('@/app/modules/conversion-report/conversion-report.routes').then(
            (m) => m.conversionReportRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.CONVERSION_REPORT.VIEW],
        },
      },
      {
        path: 'daily-call-report',
        loadChildren: () =>
          import('@/app/modules/daily-call-report/daily-call-report.routes').then(
            (m) => m.dailyCallReportRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.DAILY_CALL_REPORT.VIEW],
        },
      },
      {
        path: 'reports/daily-calls',
        loadChildren: () =>
          import('@/app/modules/daily-call-report/daily-call-report.routes').then(
            (m) => m.dailyCallReportRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.DAILY_CALL_REPORT.VIEW],
        },
      },
      {
        path: 'integration-setting-config',
        loadChildren: () =>
          import('@/app/modules/integration-setting-config/integration-setting-config.routes').then(
            (m) => m.integrationSettingRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.INTEGRATION_CONFIG.VIEW],
        },
      },
      {
        path: 'integration-sync-log',
        loadChildren: () =>
          import('@/app/modules/integration-sync-log/integration-sync-log.routes').then(
            (m) => m.integrationSyncLogsRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.INTEGRATION_SYNC_LOG.VIEW],
        },
      },
      {
        path: 'integration-sync-alert',
        loadChildren: () =>
          import('@/app/modules/integration-sync-alert/integration-sync-alert.routes').then(
            (m) => m.integrationSyncAlertRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.INTEGRATION_SYNC_ALERT.VIEW],
        },
      },

      {
        path: 'project-master',
        loadChildren: () =>
          import('@/app/modules/project-master/project-master.routes').then(
            (m) => m.projectMasterRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.PROJECT.VIEW],
        },
      },

      {
        path: 'property-configurations',
        loadChildren: () =>
          import('@/app/modules/property-configurations/property-configurations.routes').then(
            (m) => m.propertyConfigurationsRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.PROPERTY_CONFIGURATION.VIEW],
        },
      },
      {
        path: 'reports/follow-ups-pending',
        loadChildren: () =>
          import('@/app/modules/follow-up-pending-report/follow-up-pending-report.routes').then(
            (m) => m.followUpPendingReportRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.FOLLOW_UP_PENDING_REPORT.VIEW],
        },
      },
      {
        path: 'disposition-categories',
        loadChildren: () =>
          import('@/app/modules/disposition-categories/disposition-categories.routes').then(
            (m) => m.dispositionCategoriesRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.DISPOSITION_CATEGORY.VIEW],
        },
      },
      {
        path: 'area-master',
        loadChildren: () =>
          import('./modules/area-master/area-master.routes').then((m) => m.areaMasterRoutes),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.AREA_MASTER.VIEW],
        },
      },

      {
        path: 'floor-details',
        loadChildren: () =>
          import('@/app/modules/floor-details/floor-details.routes').then(
            (m) => m.floorDetailRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.FLOOR_DETAIL.VIEW],
        },
      },

      {
        path: 'furnishing-status',
        loadChildren: () =>
          import('@/app/modules/furnishing-status/furnishing-status.routes').then(
            (m) => m.furnishingStatusRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.FURNISHING_STATUS.VIEW],
        },
      },
      {
        path: 'property-purpose',
        loadChildren: () =>
          import('@/app/modules/property-purpose/property-purpose.routes').then(
            (m) => m.propertyPurposeRoutes,
          ),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.PROPERTY_PURPOSE.VIEW],
        },
      },
      {
        path: 'possession',
        loadChildren: () =>
          import('@/app/modules/possession/possession.routes').then((m) => m.possessionRoutes),
        data: {
          PERMISSION_LIST: [PERMISSION_LIST.POSSESSION.VIEW],
        },
      },
    ],
  },
  { path: '**', redirectTo: 'admin/login' },
];
